/**
 * Adapted from PlayCanvas Engine's scripts/esm/gsplat/gsplat-flipbook.mjs at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */

import type {
  GSplatFlipbookAssetCache,
  GSplatFlipbookAssetLease,
} from '@/plugins/gsplat/animation/GSplatFlipbookAssetCache'
import type { GSplatResourceLease } from '@/plugins/gsplat/GSplatHandleBinding'
import type {
  GaussianSplatHandle,
  GaussianSplatLoadRequest,
} from '@/plugins/gsplat/types/PluginTypes'
import type { AssetId, Disposable } from '@/shared/types/core'
import type { LoadOptions } from '@/shared/types/gsplat'

export type GSplatFlipbookPlayMode = 'once' | 'loop' | 'bounce'

export interface GSplatFlipbookOptions {
  readonly id?: AssetId
  readonly name?: string
  readonly fps?: number
  readonly folder?: string
  readonly filenamePattern?: string
  readonly startFrame?: number
  readonly endFrame?: number
  readonly playMode?: GSplatFlipbookPlayMode
  readonly playing?: boolean
  readonly preloadCount?: number
  readonly format?: string
  readonly loaderOptions?: Readonly<Record<string, unknown>>
  readonly loadOptions?: Omit<
    LoadOptions,
    'signal' | 'nodeId' | 'onLoadComplete' | 'telemetry' | 'autoAddToScene'
  >
}

export interface GSplatFlipbookRuntime {
  createWithResource(
    request: Readonly<GaussianSplatLoadRequest>,
    resource: GSplatResourceLease
  ): GaussianSplatHandle
  replaceWithResource(
    id: AssetId,
    resource: GSplatResourceLease,
    metadata?: { readonly url?: string; readonly name?: string }
  ): GaussianSplatHandle
  mount(handle: GaussianSplatHandle): void
  unmount(id: AssetId): void
  disposeAsset(id: AssetId): void
}

interface PreloadedFrame {
  readonly frameNum: number
  readonly url: string
  readonly lease: GSplatFlipbookAssetLease
}

export class GSplatFlipbookError extends Error {
  constructor(
    readonly code: 'GSPLAT_FLIPBOOK_INVALID_CONFIGURATION' | 'GSPLAT_FLIPBOOK_FRAME_OUT_OF_RANGE',
    readonly field: string,
    readonly actual: unknown,
    message: string
  ) {
    super(message)
    this.name = 'GSplatFlipbookError'
  }
}

/** FrameDriver Update phase 驱动的 backend-neutral GSplat Flipbook。 */
export class GSplatFlipbookController implements Disposable {
  fps: number
  folder: string
  filenamePattern: string
  startFrame: number
  endFrame: number
  playMode: GSplatFlipbookPlayMode
  playing: boolean
  preloadCount: number

  private readonly id?: AssetId
  private readonly name?: string
  private readonly format?: string
  private readonly loaderOptions?: Readonly<Record<string, unknown>>
  private readonly loadOptions?: Omit<
    LoadOptions,
    'signal' | 'nodeId' | 'onLoadComplete' | 'telemetry' | 'autoAddToScene'
  >
  private currentFrameValue: number
  private frameTimeValue = 0
  private directionValue = 1
  private currentLease: GSplatFlipbookAssetLease | null = null
  private pendingLease: GSplatFlipbookAssetLease | null = null
  private handleValue: GaussianSplatHandle | null = null
  private preloadedFrames: PreloadedFrame[] = []
  private requestGeneration = 0
  private initialization: Promise<GaussianSplatHandle> | null = null
  private errorValue: unknown = null
  private disposed = false

  constructor(
    private readonly runtime: GSplatFlipbookRuntime,
    private readonly cache: GSplatFlipbookAssetCache,
    options: GSplatFlipbookOptions = {},
    private readonly onDispose?: (controller: GSplatFlipbookController) => void
  ) {
    this.fps = options.fps ?? 30
    this.folder = options.folder ?? ''
    this.filenamePattern = options.filenamePattern ?? 'frame_{frame:04}.sog'
    this.startFrame = options.startFrame ?? 1
    this.endFrame = options.endFrame ?? 100
    this.playMode = options.playMode ?? 'loop'
    this.playing = options.playing ?? true
    this.preloadCount = options.preloadCount ?? 10
    this.id = options.id
    this.name = options.name
    this.format = options.format
    this.loaderOptions = options.loaderOptions
    this.loadOptions = options.loadOptions
    this.currentFrameValue = this.startFrame
    this.validateConfiguration()
  }

  get currentFrame(): number {
    return this.currentFrameValue
  }

  get frameTime(): number {
    return this.frameTimeValue
  }

  get direction(): number {
    return this.directionValue
  }

  get handle(): GaussianSplatHandle | null {
    return this.handleValue
  }

  get lastError(): unknown {
    return this.errorValue
  }

  get preloadLength(): number {
    return this.preloadedFrames.length
  }

  get preloadedFrameNumbers(): readonly number[] {
    return Object.freeze(this.preloadedFrames.map(({ frameNum }) => frameNum))
  }

  get ready(): Promise<GaussianSplatHandle> {
    return this.initialize()
  }

  initialize(): Promise<GaussianSplatHandle> {
    this.assertActive()
    this.initialization ??= this.loadFrame(this.startFrame)
    return this.initialization
  }

  update(dt: number): void {
    this.assertActive()
    this.validateConfiguration()
    if (!Number.isFinite(dt) || dt < 0) {
      throw new GSplatFlipbookError(
        'GSPLAT_FLIPBOOK_INVALID_CONFIGURATION',
        'dt',
        dt,
        'GSplatFlipbook dt 必须是非负有限数'
      )
    }
    if (!this.playing) return

    this.frameTimeValue += dt
    if (this.frameTimeValue >= 1 / this.fps) {
      this.frameTimeValue = 0
      if (this.preloadedFrames[0]?.lease.ready) this.switchToNextFrame()
    }
  }

  switchToNextFrame(): boolean {
    this.assertActive()
    const next = this.preloadedFrames[0]
    if (!next || !next.lease.ready || !this.handleValue) return false

    let resource: GSplatResourceLease | null = null
    try {
      resource = next.lease.acquireResource()
      const staged = resource
      resource = null
      this.runtime.replaceWithResource(this.handleValue.id, staged, {
        url: next.url,
        name: this.name,
      })
    } catch (error) {
      resource?.dispose()
      this.errorValue = error
      return false
    }

    this.preloadedFrames.shift()
    this.currentLease?.dispose()
    this.currentLease = next.lease
    this.advanceFrame()
    this.errorValue = null
    this.maintainPreloadBuffer()
    return true
  }

  advanceFrame(): void {
    this.assertActive()
    if (this.playMode === 'bounce') {
      this.currentFrameValue += this.directionValue
      if (this.currentFrameValue >= this.endFrame) {
        this.currentFrameValue = this.endFrame
        this.directionValue = -1
      } else if (this.currentFrameValue <= this.startFrame) {
        this.currentFrameValue = this.startFrame
        this.directionValue = 1
      }
    } else if (this.playMode === 'loop') {
      this.currentFrameValue++
      if (this.currentFrameValue > this.endFrame) this.currentFrameValue = this.startFrame
    } else if (this.playMode === 'once') {
      this.currentFrameValue++
      if (this.currentFrameValue > this.endFrame) this.playing = false
    }
  }

  getNextFrameNumber(): number | null {
    this.validateConfiguration()
    if (this.playMode === 'bounce') {
      if (this.startFrame === this.endFrame) return this.startFrame
      return this.currentFrameValue + this.directionValue
    }
    if (this.playMode === 'loop') {
      const next = this.currentFrameValue + 1
      return next > this.endFrame ? this.startFrame : next
    }
    const next = this.currentFrameValue + 1
    return next <= this.endFrame ? next : null
  }

  getNextFrameNumberFromLast(
    frames: readonly PreloadedFrame[] = this.preloadedFrames
  ): number | null {
    this.validateConfiguration()
    if (frames.length === 0) return this.getNextFrameNumber()
    const lastFrame = frames[frames.length - 1].frameNum

    if (this.playMode === 'bounce') {
      if (this.startFrame === this.endFrame) return this.startFrame
      let checkFrame = this.currentFrameValue
      let checkDirection = this.directionValue
      while (checkFrame !== lastFrame) {
        checkFrame += checkDirection
        if (checkFrame >= this.endFrame) {
          checkFrame = this.endFrame
          checkDirection = -1
        } else if (checkFrame <= this.startFrame) {
          checkFrame = this.startFrame
          checkDirection = 1
        }
      }
      const next = lastFrame + checkDirection
      if (next >= this.startFrame && next <= this.endFrame) return next
      if (next > this.endFrame) return this.endFrame
      if (next < this.startFrame) return this.startFrame
      return next
    }
    if (this.playMode === 'loop') {
      const next = lastFrame + 1
      return next > this.endFrame ? this.startFrame : next
    }
    const next = lastFrame + 1
    return next <= this.endFrame ? next : null
  }

  maintainPreloadBuffer(): void {
    this.assertActive()
    this.validateConfiguration()
    const staged: PreloadedFrame[] = []
    try {
      while (this.preloadedFrames.length + staged.length < this.preloadCount) {
        const frames = [...this.preloadedFrames, ...staged]
        const nextFrameNum = this.getNextFrameNumberFromLast(frames)
        if (nextFrameNum === null) break
        const url = this.getFramePath(nextFrameNum)
        const lease = this.cache.acquire(this.createRequest(url))
        staged.push({ frameNum: nextFrameNum, url: lease.url, lease })
      }
      this.preloadedFrames.push(...staged)
      for (const frame of staged) {
        void frame.lease.waitUntilReady().catch((error: unknown) => {
          if (!this.disposed && this.preloadedFrames.some(({ lease }) => lease === frame.lease)) {
            this.errorValue = error
          }
        })
      }
    } catch (error) {
      for (const frame of [...staged].reverse()) frame.lease.dispose()
      this.errorValue = error
    }
  }

  async loadFrame(frameNum: number): Promise<GaussianSplatHandle> {
    this.assertActive()
    this.validateFrame(frameNum)
    this.validateConfiguration()
    const generation = ++this.requestGeneration
    this.pendingLease?.dispose()

    const url = this.getFramePath(frameNum)
    const lease = this.cache.acquire(this.createRequest(url))
    this.pendingLease = lease
    try {
      await lease.waitUntilReady()
      return this.onFrameReady(generation, frameNum, lease)
    } catch (error) {
      lease.dispose()
      if (this.pendingLease === lease) this.pendingLease = null
      if (generation === this.requestGeneration && !this.disposed) this.errorValue = error
      throw error
    }
  }

  onFrameReady(
    generation: number,
    frameNum: number,
    lease: GSplatFlipbookAssetLease
  ): GaussianSplatHandle {
    if (this.disposed || generation !== this.requestGeneration || this.pendingLease !== lease) {
      lease.dispose()
      throw abortError()
    }

    let resource: GSplatResourceLease | null = lease.acquireResource()
    try {
      const staged = resource
      resource = null
      if (this.handleValue) {
        this.runtime.replaceWithResource(this.handleValue.id, staged, {
          url: lease.url,
          name: this.name,
        })
      } else {
        const handle = this.runtime.createWithResource(this.createRequest(lease.url), staged)
        try {
          this.runtime.mount(handle)
        } catch (error) {
          this.runtime.disposeAsset(handle.id)
          throw error
        }
        this.handleValue = handle
      }
    } catch (error) {
      resource?.dispose()
      throw error
    }

    const oldCurrent = this.currentLease
    const oldQueue = this.preloadedFrames
    this.currentLease = lease
    this.pendingLease = null
    this.preloadedFrames = []
    this.currentFrameValue = frameNum
    this.errorValue = null
    oldCurrent?.dispose()
    for (const frame of oldQueue) frame.lease.dispose()
    this.maintainPreloadBuffer()
    return this.handleValue
  }

  getFramePath(frameNum: number): string {
    this.validateConfiguration()
    const filename = this.filenamePattern.replace(
      /\{frame(?::(\d+))?\}/g,
      (_match, padding: string | undefined) =>
        padding
          ? frameNum.toString().padStart(Number.parseInt(padding, 10), '0')
          : frameNum.toString()
    )
    const folder = this.folder && !this.folder.endsWith('/') ? `${this.folder}/` : this.folder
    return `${folder}${filename}`
  }

  play(): void {
    this.assertActive()
    this.playing = true
  }

  pause(): void {
    this.assertActive()
    this.playing = false
  }

  stop(): Promise<GaussianSplatHandle> {
    this.assertActive()
    this.playing = false
    this.directionValue = 1
    this.frameTimeValue = 0
    return this.loadFrame(this.startFrame)
  }

  seekToFrame(frameNum: number): Promise<GaussianSplatHandle> {
    this.assertActive()
    this.validateFrame(frameNum)
    this.frameTimeValue = 0
    return this.loadFrame(frameNum)
  }

  mount(): void {
    this.assertActive()
    if (!this.handleValue) throw new Error('GSplatFlipbook 尚未初始化')
    this.runtime.mount(this.handleValue)
  }

  unmount(): void {
    if (this.handleValue) this.runtime.unmount(this.handleValue.id)
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.requestGeneration++
    const current = this.currentLease
    const queue = this.preloadedFrames
    const pending = this.pendingLease
    const handle = this.handleValue
    this.currentLease = null
    this.preloadedFrames = []
    this.pendingLease = null
    this.handleValue = null

    current?.dispose()
    for (const frame of queue) frame.lease.dispose()
    pending?.dispose()
    if (handle) this.runtime.disposeAsset(handle.id)
    this.onDispose?.(this)
  }

  private createRequest(url: string): Readonly<GaussianSplatLoadRequest> {
    return Object.freeze({
      url,
      ...(this.id ? { id: this.id } : {}),
      ...(this.name ? { name: this.name } : {}),
      ...(this.format ? { format: this.format } : {}),
      ...(this.loaderOptions ? { loaderOptions: this.loaderOptions } : {}),
      ...(this.loadOptions ? { options: this.loadOptions } : {}),
    })
  }

  private validateConfiguration(): void {
    if (!Number.isFinite(this.fps) || this.fps <= 0) this.invalid('fps', this.fps, '必须是有限正数')
    if (!Number.isSafeInteger(this.preloadCount) || this.preloadCount < 0) {
      this.invalid('preloadCount', this.preloadCount, '必须是非负整数')
    }
    if (!Number.isSafeInteger(this.startFrame)) {
      this.invalid('startFrame', this.startFrame, '必须是有限整数')
    }
    if (!Number.isSafeInteger(this.endFrame)) {
      this.invalid('endFrame', this.endFrame, '必须是有限整数')
    }
    if (this.startFrame > this.endFrame) {
      this.invalid(
        'frameRange',
        `${this.startFrame}..${this.endFrame}`,
        'startFrame 不能大于 endFrame'
      )
    }
    if (this.playMode !== 'once' && this.playMode !== 'loop' && this.playMode !== 'bounce') {
      this.invalid('playMode', this.playMode, '必须是 once/loop/bounce')
    }
    if (typeof this.folder !== 'string') this.invalid('folder', this.folder, '必须是字符串')
    if (typeof this.filenamePattern !== 'string') {
      this.invalid('filenamePattern', this.filenamePattern, '必须是字符串')
    }
  }

  private validateFrame(frameNum: number): void {
    if (!Number.isSafeInteger(frameNum) || frameNum < this.startFrame || frameNum > this.endFrame) {
      throw new GSplatFlipbookError(
        'GSPLAT_FLIPBOOK_FRAME_OUT_OF_RANGE',
        'frameNum',
        frameNum,
        `GSplatFlipbook frame ${frameNum} 超出 [${this.startFrame}, ${this.endFrame}]`
      )
    }
  }

  private invalid(field: string, actual: unknown, reason: string): never {
    throw new GSplatFlipbookError(
      'GSPLAT_FLIPBOOK_INVALID_CONFIGURATION',
      field,
      actual,
      `GSplatFlipbook ${field} ${reason}`
    )
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('GSplatFlipbookController 已销毁')
  }
}

function abortError(): DOMException {
  return new DOMException('Aborted', 'AbortError')
}
